<script lang="ts">
import type { InstantWinDraw } from '@/types'
import type { TIconValue } from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'

import AppCarousel from './AppCarousel.vue'
import CarouselItem from './CarouselItem.vue'

export default defineComponent({
  components: {
    AppCarousel,
    CarouselItem,
  },
  props: {
    games: {
      type: Array as PropType<Array<InstantWinDraw>>,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      required: true,
    },
    remainingColumns: {
      type: Number,
      default: 0,
    },
    showViewAll: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['previousPage', 'nextPage'],
  data() {
    return {
      currentPageIndex: 0,
    }
  },
  computed: {
    mobilePaginatedGames(): Array<Array<InstantWinDraw>> {
      // Split games into pages of 4 (2x2 grid per page)
      const pages: Array<Array<InstantWinDraw>> = []
      const gamesPerPage = 4

      for (let i = 0; i < this.games.length; i += gamesPerPage) {
        pages.push(this.games.slice(i, i + gamesPerPage))
      }

      return pages.length > 0 ? pages : [[]]
    },
    emptyColumnsInGrid(): number {
      // Calculate how many columns to span in the 2-column grid
      // If remainingColumns is 1 or 3, card should span 1 column
      // If remainingColumns is 2, card should span 2 columns
      if (this.remainingColumns === 0) {
        return 0
      }
      return this.remainingColumns === 2 ? 2 : 1
    },
  },
  methods: {
    previousPage() {
      if (this.currentPageIndex > 0) {
        this.currentPageIndex--
        this.scrollToPage()
      }
      this.$emit('previousPage', this.currentPageIndex)
    },
    nextPage() {
      if (this.currentPageIndex < this.mobilePaginatedGames.length - 1) {
        this.currentPageIndex++
        this.scrollToPage()
      }
      this.$emit('nextPage', this.currentPageIndex)
    },
    scrollToPage() {
      const carousel = this.$refs.carousel as any
      carousel?.scrollbox?.scrollTo(this.currentPageIndex)
    },
  },
})
</script>

<template>
  <div>
    <AppCarousel
      ref="carousel"
      :show-navigation="true"
      :title
      :icon="icon as TIconValue"
      :view-all-route="
        showViewAll
          ? { name: 'prizes', query: { switch: 'instant-wins' } }
          : undefined
      "
      frame-class="mobile-game-carousel"
    >
      <template #preamble>
        <slot name="preamble"></slot>
      </template>

      <CarouselItem
        v-for="(page, pageIndex) in mobilePaginatedGames"
        :key="`page-${pageIndex}`"
        class="w-full"
      >
        <div class="grid grid-cols-2 gap-3">
          <slot :games="page" :page-index="pageIndex" :page="page" />
          <slot
            v-if="
              pageIndex === mobilePaginatedGames.length - 1 &&
              remainingColumns > 0
            "
            name="empty"
            :empty-slots="emptyColumnsInGrid"
          ></slot>
        </div>
      </CarouselItem>
    </AppCarousel>
  </div>
</template>
